import React from 'react';
import Cookies from 'js-cookie';
import { CertActions } from '@/models/Cert/CertActions';
import { CertRequestBuilder } from '@/models/Cert/CertRequestBuilder';
import MemberActions from '@/models/Member/MemberActions';
import { MemberNewActions } from '@/models/Member/MemberNewActions';
import { MemberNewRequestBuilder } from '@/models/Member/MemberNewRequestBuilder';
import MemberRequestBuilder from '@/models/Member/MemberRequestBuilder';
import { SessionActions } from '@/models/Session/SessionActions';
import { SessionRequestBuilder } from '@/models/Session/SessionRequestBuilder';
import { Kinesis } from '@/services/TrackerService';
import GlobalStore from '@/stores/GlobalStore';
import { IMarketingTemrsStoreProps, MarketingTermsStore } from '@/stores/partial/MarketingTermsStore';
import { debounce, getRunMode, redirectTo } from '@/utils';
import { EN_APP_DEVICE } from '@/utils/functions/device';
import { scrollWithAnim } from '@/utils/functions/dom/scrollWithAnim';
import extractAPIData from '@/utils/functions/extractAPIdata';
import Future from '@/utils/Future';
import { getYaNative } from '@yanolja-org/ya-native';
import {
  AppVersionCallbackParam,
  PushStatusCallbackParam,
  RequestPushAuthorizationCallbackParam,
} from '@yanolja-org/ya-native/dist/interfaces';
import { action, observable } from 'mobx';
import qs from 'qs';
import { identity, prop } from 'ramda';
import { createContext } from 'react';
import { isFeatureEnabled } from '@/utils/feature-flag/is-feature-enabled';
import type { PublicPassAuthReturn, UIHandlers } from '@yanolja-org/nol-identity-verification-sdk';
import { ConnectPassAuthSdk } from '@yanolja-org/nol-identity-verification-sdk';
import { IPopupProps } from '@/components/Common/DesignSystem/Popup/Popup';

const CHECK_AGREEMENT_COOKIE_KEY = 'plcc_agreement';

interface NolPromotionStoreProps {
  isLoggedIn: boolean;
  hasCI: boolean;
  promotionUrl: string;
  promotionDeeplink: string;
  homeUrl: string;
  myYanoljaPath: string;
  loginPath: string;
  eventcode: string;
  hashedMemberNo: string;
  hyCardPath: string;
  returnUrl: string;
  globalStore: GlobalStore;
  marketingTermsProps: IMarketingTemrsStoreProps;
  deviceOS: 'MW' | 'PW' | 'IOS' | 'AND';
  plccSplitSite: string;
  plccInterparkSite: string;
  plccTripleSite: string;
  nickname: string;
  version: string;
  rcno: string;
  nolPointTermsUrl: string;
}

enum CONNECT_STATUS {
  CONNECTED = 200,
  CONNECTION_COMPLETE = 201,
  CONNECTION_COMPLETE_BUT_BEFORE_REGISTER = 202,
  NOT_EXIST_MEMBER = 400,
  NOT_EXIST_LINKAGE = 404,
  NOT_EXIST_CI = 409,
}

// 본인인증 sdk 커스텀 메시지
const PASS_AUTH_SDK_RESULT_CODE_MESSAGE = {
  UNDER_14_YEARS: '만 14세 이상 회원만 이용 가능합니다',
  EXIST_AUTHENTICATION_IN_OTHER_MEMBER:
    'NOL 계정에 이미 등록된 본인인증 정보입니다. 기존에 인증 받은 계정으로 참여해 주세요',
};

export class NolPromotionStore {
  @observable hasCICert: boolean;
  @observable activeIndex: number;
  @observable showSmartBanner: boolean;
  @observable anchorTab0Ref: React.RefObject<HTMLDivElement> | null;
  @observable anchorTab1Ref: React.RefObject<HTMLDivElement> | null;
  @observable anchorTab2Ref: React.RefObject<HTMLDivElement> | null;
  @observable anchorTab3Ref: React.RefObject<HTMLDivElement> | null;
  @observable specialPriceRef: HTMLElement | null;
  /* 이미 발급된 카드 안내 바텀시트 */
  @observable isAlreadyHasCardBottomSheetOpen: boolean;
  /* 놀카드를 발급받았지만 수령 등록하기 이전 상태 안내 바텀시트 */
  @observable isAlreadyHasCardBeforeRegisterBottomSheetOpen: boolean = false;
  /* CI 인증 요청 바텀시트 */
  @observable isCIBottomSheetOpen: boolean;
  /* 가입 완료 바텀시트 */
  @observable isSignUpCompleteOpen: boolean;
  /* 포인트 유의사항 바텀시트 */
  @observable isPointBottomSheetOpen: boolean;
  /* 2만원 캐시백(구 청구할인) 유의사항 바텀시트 */
  @observable isClaimDiscountBottomSheetOpen: boolean;
  /* 야놀자에서 첫 결제시 10만원 캐시백 자세히보기 바텀시트 */
  @observable isNolYanoljaCashbackBottomSheetOpen: boolean;
  /* 할인 쿠폰 유의사항 바텀시트 */
  @observable isDiscountCouponBottomSheetOpen: boolean;
  /* 골드 클래스 쿠폰 정보 바텀시트 */
  @observable isGoldClassCouponBottomSheetOpen: boolean;
  /* 라운지 유의사항 바텀시트 */
  @observable isLoungeBottomSheetOpen: boolean;
  /* 토핑 멤버십 유의사항 바텀시트 */
  @observable isTopingMemberShipBottomSheetOpen: boolean;
  /* 야놀자 코인 유의사항 바텀시트 */
  @observable isYanoljaCoinBottomSheetOpen: boolean;
  /* 애플페이 바텀시트 */
  @observable isApplePayBottomSheetOpen: boolean;
  /* 놀카드 항공특가 바텀시트 */
  @observable isFlightSpecialBottomSheetOpen: boolean;
  /* 놀굿즈 유의사항 바텀시트 오픈 여부*/
  @observable isNolGoodsBottomSheetOpen: boolean;
  /* 트리플 할인 쿠폰 바텀시트 오픈 여부 */
  @observable isTripleDiscountCouponBottomSheetOpen: boolean;
  /* 트리플 할인 쿠폰 바텀시트 오픈 여부 */
  @observable isNolCardFlightOffersBottomSheetOpen: boolean;
  /* 캐시백 유의사항 바텀시트 */
  @observable isCashbackBottomSheetOpen: boolean;
  /* 0903버전 약관동의 모달(필수 없는 모달) */
  @observable isAgreement0903Open: boolean;
  /* 약관동의 모달 */
  @observable isAgreementOpen: boolean;
  /* CI 진행 여부 확인 팝업 */
  @observable isCIConfirmPopupOpen: boolean;
  /* CI 인증 실패 확인 팝업 */
  @observable isCIFailPopupOpen: boolean;
  /* 로그인 진행 여부 확인 팝업 */
  @observable isLoginPopupOpen: boolean;
  /* 약관 동의 완료 팝업 오픈 여부 */
  @observable isAgreementPopupOpen: boolean;
  /* 공유하기 모달 오픈 여부 */
  @observable isShareModalOpen: boolean;
  /* 진입 불가 모달 오픈 여부 */
  @observable isNotAvailableModalOpen: boolean;
  /* 팝업 차단 안내 모달 오픈 여부 */
  @observable isPopupBlockModalOpen: boolean;

  /* 본인 인증 진행 윈도우 객체 */
  @observable certWindow: Window | null;

  @observable eventNolCouponPeriod: string; // 놀 쿠폰 이벤트 기간
  @observable eventPeriod: string; // 이번 이벤트 기간
  @observable eventCashbackPeriod: string; // 캐시백 이벤트 기간(이것만 다름)
  @observable eventBefore6MonthPeriod: string; // 이번 이벤트 이전 6개월 기간
  @observable eventDisabledCashbackList: string[]; // 캐시백 적용 불가 케이스 e.g. 23년 4월 18일부터 24년 1월 29일까지 진행된 NOL 카드 첫결제 청구할인 이벤트
  @observable eventCashbackDate: string; // 캐시백 지급 시기. e.g. 12월 말일 경

  @observable sdkShowDialogState: IPopupProps; // 본인인증 sdk에 주입할 showDialog 상태값. popup component props 형태로 변환하여 관리한다.
  readonly promotionUrl: string;
  readonly promotionDeeplink: string;
  readonly isLoggedIn: boolean;
  readonly myYanoljaPath: string;
  readonly loginPath: string;
  readonly eventcode: string;
  readonly hashedMemberNo: string;
  readonly hyCardPath: string;
  readonly returnUrl: string;
  readonly plccSplitSite: string;
  readonly plccInterparkSite: string;
  readonly plccTripleSite: string;
  readonly marketingTermsStore: MarketingTermsStore;
  readonly nickname: string;
  readonly pageName = 'MotherPage';
  readonly homeUrl: string;
  /* 추천인 id  */
  readonly rcno: string;
  /* 프로모션 페이지 버전 */
  readonly version: string;
  /* 놀 포인트 약관 페이지 */
  readonly nolPointTermsUrl: string;

  /* 약관 동의 여부 확인시 까지 기다림 */
  waitForAgreement: Future<boolean> | undefined;
  /* CI 인증 요청 결정 팝업 기다림*/
  waitForCIPopup: Future<boolean> | undefined;
  /* CI 완료시점까지 기다림 */
  waitForCI: Future<boolean> | undefined;
  /* 카드 발급 여부 기다림 */
  waitForCardIssued: Future<boolean> | undefined;
  /* 로그인 요청 결정 팝업 기다림 */
  waitForLoginPopup: Future<boolean> | undefined;
  /* 약관동의 후 완료 팝업 기다림 */
  waitForAgreementPopup: Future<boolean> | undefined;
  /* 신규 본인인증 모듈*/
  private passAuthSdk: PublicPassAuthReturn;

  /**
   * showToast 커스텀 핸들러
   * */
  private customShowToast: UIHandlers['showToast'] = (props) => {
    this.globalStore.toast.addItem({
      key: props?.id ?? 'toast-id',
      // text가 string이 아닐 경우 default 값으로 '알림' 설정
      text: typeof props.children === 'string' ? props.children : '알림',
      timeout: props?.duration ?? 3000,
    });
  };
  /**
   * showDialog 커스텀 핸들러
   * */
  private customShowDialog: UIHandlers['showDialog'] = (props) => {
    this.sdkShowDialogState = {
      isOpen: true,
      children: (
        <div
          key={props?.id ?? 'dialog-id'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <div>{props.title}</div>
          {props.description && <div style={{ marginTop: '8px' }}>{props.description}</div>}
        </div>
      ),
      mainBtn: {
        label: typeof props?.positiveButton?.text === 'string' ? props.positiveButton.text : '확인',
        onClick: () => {
          props?.positiveButton?.onClick?.();
        },
      },
      subBtn: props.negativeButton
        ? {
            label: typeof props?.negativeButton?.text === 'string' ? props.negativeButton.text : '취소',
            onClick: () => {
              props?.negativeButton?.onClick?.();
            },
          }
        : undefined,
    };
    return {
      destroy: () => {
        this.sdkShowDialogState.isOpen = false;
      },
    };
  };
  private memberNewActions = new MemberNewActions(MemberNewRequestBuilder);
  private memberActions = new MemberActions(MemberRequestBuilder);
  private certActions = new CertActions(CertRequestBuilder);
  private globalStore: GlobalStore;
  // private ciChecker?: CIChecker;
  private hyCardWindow: Window | null = null;
  /* 카드 발급 여부 */
  private isHyCardIssued = false;
  private isDevicePushAgreed: boolean | undefined = undefined;
  /* CI 혹은 약관 동의 완료 후 현대카드 홈페이지 진입 전 혜택받기 버튼 클릭시 발생하는 플로우 꼬임 문제 수정을 위해 추가 */
  private disableToMoveHyCard = false;
  /* 놀 약관 동의 여부 */
  private isNolAgreed = false;
  constructor({
    isLoggedIn,
    hasCI,
    promotionUrl,
    promotionDeeplink,
    homeUrl,
    myYanoljaPath,
    loginPath,
    eventcode,
    hashedMemberNo,
    hyCardPath,
    returnUrl,
    marketingTermsProps,
    globalStore,
    plccSplitSite,
    plccInterparkSite,
    plccTripleSite,
    nickname,
    version,
    rcno,
    nolPointTermsUrl,
  }: NolPromotionStoreProps) {
    this.isLoggedIn = isLoggedIn;
    this.hasCICert = hasCI;
    this.promotionUrl = promotionUrl;
    this.promotionDeeplink = `yanoljamotel://${promotionDeeplink}`;
    this.homeUrl = homeUrl;
    this.myYanoljaPath = myYanoljaPath;
    this.loginPath = loginPath;
    this.eventcode = eventcode;
    this.hashedMemberNo = hashedMemberNo;
    this.hyCardPath = hyCardPath;
    this.returnUrl = returnUrl;
    this.globalStore = globalStore;
    this.plccSplitSite = plccSplitSite;
    this.plccInterparkSite = plccInterparkSite;
    this.plccTripleSite = plccTripleSite;
    this.nickname = nickname;
    this.isNolAgreed = globalStore.Agreements?.member?.nol || false;
    this.version = version;

    this.activeIndex = -1;

    this.isAlreadyHasCardBottomSheetOpen = false;
    this.isAgreementOpen = false;
    this.isAgreementPopupOpen = false;
    this.isCIFailPopupOpen = false;
    this.showSmartBanner = globalStore.ShowSmartBanner;

    this.marketingTermsStore = new MarketingTermsStore({ ...marketingTermsProps });
    this.rcno = rcno;
    this.nolPointTermsUrl = nolPointTermsUrl;
    /** sunny-web config 타입과 passAuthSdk에 정의된 config 맵핑 */
    const ENV_CONFIG_MAP: Record<ReturnType<typeof getRunMode>, Parameters<typeof ConnectPassAuthSdk>[0]['config']> = {
      local: 'local',
      development: 'dev',
      qa: 'qa',
      stage: 'stage',
      production: 'prod',
    };

    // 본인인증 SDK 초기화를 위한 ui 인스턴스 정의
    this.passAuthSdk = ConnectPassAuthSdk({
      config: ENV_CONFIG_MAP[getRunMode()],
      caller: 'sunny-web',
      ui: {
        showToast: this.customShowToast,
        showDialog: this.customShowDialog,
      },
    });
  }

  login() {
    const yaNative = getYaNative();
    if (yaNative.isEnabled) {
      yaNative.login({ method: 'GET', url: location.href });
    } else {
      window.location.href = this.loginPath;
    }
  }

  @action.bound
  async moveToHyunDaiCard(): Promise<void> {
    if (this.disableToMoveHyCard) {
      return;
    }
    this.waitForAgreement = new Future<boolean>();
    this.waitForCIPopup = new Future<boolean>();
    this.waitForCI = new Future<boolean>();
    this.waitForCardIssued = new Future<boolean>();
    this.waitForLoginPopup = new Future<boolean>();
    this.waitForAgreementPopup = new Future<boolean>();

    /* 로그인 여부 확인 */
    if (!this.isLoggedIn) {
      this.isLoginPopupOpen = true;
      const shouldLogin = await this.waitForLoginPopup.get();
      if (shouldLogin) {
        // 닫히는 애니메이션이 끝난 후 로그인 페이지로 이동
        setTimeout(() => this.login(), 300);
      }
      this.isLoginPopupOpen = false;
      return;
    }

    /* 약관 동의 */
    if (parseInt(this.version) >= 16) {
      if (!Cookies.get(CHECK_AGREEMENT_COOKIE_KEY)) {
        this.isAgreement0903Open = true;
        const isAgreed = await this.waitForAgreement.get();
        this.isAgreement0903Open = false;
        if (!isAgreed) {
          return;
        }
        // 1회 동의시 30일 유지
        Cookies.set(CHECK_AGREEMENT_COOKIE_KEY, '1', {
          expires: 30,
        });
      }
    } else if (!this.isNolAgreed) {
      this.isAgreementOpen = true;
      const isAgreed = await this.waitForAgreement.get();
      this.isAgreementOpen = false;
      if (!isAgreed) {
        return;
      }
      this.isNolAgreed = true;
    }

    /* CI 인증*/
    if (!this.hasCICert) {
      this.isCIConfirmPopupOpen = true;
      const isConfirm = await this.waitForCIPopup.get();
      this.isCIConfirmPopupOpen = false;
      if (!isConfirm) {
        return;
      }
      this.openCIPopup();
      // new pass auth sdk 적용시 popup 창 오픈 체크 로직이 내부에 존재.
      if (!isFeatureEnabled('YAWEB-6561--plcc-new-pass-auth')) {
        if (!this.certWindow && !this.globalStore.AppDevice) {
          // 팝업 오픈 실패
          this.isPopupBlockModalOpen = true;
          return;
        }
      }
      const isCertSuccess = await this.waitForCI.get();
      if (!isCertSuccess) {
        this.isCIFailPopupOpen = true;
        this.disableToMoveHyCard = false;
        return;
      }
      this.hasCICert = true;
    }

    this.disableToMoveHyCard = true;

    /* 약관 동의 여부 저장 */
    /* 필수 항목만 존재하므로 약관동의, 개인정보 활용동의 여부를 둘다 저장한다 */
    const chagngeAgree = this.memberActions.changeAgree({
      body: {
        device_os: this.globalStore.IsPc ? 'PW' : 'MW',
        agree: {
          member: {
            nol: true,
          },
          certification: true,
        },
      },
    });

    /* 현대카드 가입여부 확인 */
    const nolcheckConnect = this.memberNewActions.nolCheckConnect({
      headers: { 'x-api-key': '' },
      resources: { memberNo: 'member' },
    });

    const [, entity] = await Promise.all([chagngeAgree, nolcheckConnect]);

    const statusCode = entity.map(identity).caseOf({
      left: prop('statusCode'),
      right: prop('statusCode'),
    });

    // 카드 발급받았지만 수령 등록 이전
    if (statusCode === CONNECT_STATUS.CONNECTION_COMPLETE_BUT_BEFORE_REGISTER) {
      this.isAlreadyHasCardBeforeRegisterBottomSheetOpen = true;
      this.disableToMoveHyCard = false;
      return;
    }

    // 이미 카드 발급받음
    if (statusCode === CONNECT_STATUS.CONNECTED || statusCode === CONNECT_STATUS.CONNECTION_COMPLETE) {
      this.isAlreadyHasCardBottomSheetOpen = true;
      this.disableToMoveHyCard = false;
      return;
    }

    /* 현대카드 가입페이지로 이동 */
    this.openHyCardJoinPopup();
    if (!this.hyCardWindow && !this.globalStore.AppDevice) {
      // 팝업 오픈 실패
      this.isPopupBlockModalOpen = true;
      this.disableToMoveHyCard = false;
      return;
    }
    /* 현대카드 홈페이지 진입 1초 후 다시 기능 동작 할 수 있도록 변경 */
    setTimeout(() => (this.disableToMoveHyCard = false), 1000);

    /* 현대카드 발급 여부 확인*/
    const isCardIssued = await this.waitForCardIssued?.get();
    if (isCardIssued) {
      this.isSignUpCompleteOpen = true;
    }
    Kinesis.bg(this.pageName, {
      issued: isCardIssued,
      referrer: this.eventcode,
      hashedHyundaicardMember: this.rcno,
      motherPageVersion: this.version,
    });
  }

  @action.bound
  async openHyCardJoinPopup(): Promise<void> {
    // eventCode가 undefined로 진입되는 케이스가 있어 기본값 적용
    let eventCode = this.eventcode;
    if (!eventCode || eventCode === 'undefined') {
      eventCode = 'NOY92';
    }

    const params = {
      medium: 'G_M_WEB_O',
      cardWcd: 'NOLHC',
      eventCode,
      memberCorp: 'YANOLJA',
      memberNo: this.hashedMemberNo,
      returnUrl: this.returnUrl,
      rcno: this.rcno,
    };

    /* 현대카드 가입페이지로 이동 */
    const url = `${this.hyCardPath}?${qs.stringify(params)}`;
    const yaNative = getYaNative();
    if (yaNative.isEnabled) {
      /* 다시 웹뷰로 돌아 왔을때 연동 여부를 체크하여 카드 발급 여부를 확인한다 */
      const onChange = debounce(async () => {
        if (document.visibilityState === 'hidden') {
          return;
        }
        document.removeEventListener('visibilitychange', onChange);
        window.removeEventListener('pageshow', onChange);

        const sessionEntity = new SessionActions(SessionRequestBuilder).getNolConnected();
        const isIssued = (await extractAPIData(sessionEntity)) || false;
        this.isHyCardIssued = isIssued;
        this.waitForCardIssued?.complete(isIssued);
      }, 100);

      document.addEventListener('visibilitychange', onChange);
      window.addEventListener('pageshow', onChange);

      const version = await new Promise<AppVersionCallbackParam>((resolve) => {
        yaNative.appVersion((_success, info) => {
          resolve(info);
        });
      });
      const [major, minor] = version.version.split('.');
      const isNotSupportVersion =
        this.globalStore.AppDevice === EN_APP_DEVICE.IOS && Number(major) <= 8 && Number(minor) <= 57;

      if (isNotSupportVersion) {
        window.location.href = `yanoljamotel://wv?url=${url}&ya_outlink=true`;
      } else {
        yaNative.wv({
          method: 'get',
          layout: {
            modal: false,
          },
          url: this.hyCardPath,
          params,
        });
      }
    } else {
      this.hyCardWindow = window.open(url, 'hy-card');
      window.removeEventListener('message', this.handleMessage, false);
      window.addEventListener('message', this.handleMessage, false);
    }
  }

  @action.bound
  goToPointPromotion(url: string): void {
    const yaNative = getYaNative();

    if (yaNative.isEnabled) {
      yaNative.wv({
        method: 'get',
        layout: {
          modal: false,
        },
        url,
      });

      return;
    }

    location.href = url;
  }

  @action.bound
  openCIPopup(): void {
    if (isFeatureEnabled('YAWEB-6561--plcc-new-pass-auth')) {
      this.passAuthSdk.renewal({
        onSuccess: () => {
          this.onCICertSuccess();
        },
        onCustomResultCode: {
          UNDER_14_YEARS: () => {
            this.customShowDialog({
              id: 'under-14-years-dialog',
              title: PASS_AUTH_SDK_RESULT_CODE_MESSAGE.UNDER_14_YEARS,
              positiveButton: {
                text: '확인',
                onClick: () => {
                  this.sdkShowDialogState.isOpen = false;
                },
              },
            });
          },
          ALREADY_CERTIFICATED: () => {
            this.customShowDialog({
              id: 'already-certificated-dialog',
              title: PASS_AUTH_SDK_RESULT_CODE_MESSAGE.EXIST_AUTHENTICATION_IN_OTHER_MEMBER,
              positiveButton: {
                text: '확인',
                onClick: () => {
                  this.sdkShowDialogState.isOpen = false;
                },
              },
            });
          },
        },
      });
      return;
    }
    const yaNative = getYaNative();
    const target = '/ci-cert';

    const params = {
      rid: Date.now(),
      pageType: 'MAIN',
    };

    if (yaNative.isEnabled) {
      const onChange = debounce(async () => {
        if (document.visibilityState === 'hidden') {
          return;
        }
        document.removeEventListener('visibilitychange', onChange);
        window.removeEventListener('pageshow', onChange);

        const certEntity = await this.certActions.getCI({ query: { rid: params.rid } });
        certEntity.map(prop('data')).do({
          left: () => {
            this.onCICertError();
          },
          right: ({ ci }) => {
            if (ci) {
              this.onCICertSuccess();
            } else {
              this.onCICertError();
            }
          },
        });
      }, 100);

      document.addEventListener('visibilitychange', onChange);
      window.addEventListener('pageshow', onChange);

      const origin = location.origin;
      const title = '본인 인증';

      yaNative.wv({
        method: 'get',
        headers: { Referer: origin },
        url: `${origin}${target}`,
        params,
        title,
        layout: {
          modal: true, // 창 닫기 버튼
          title,
        },
      });
    } else {
      const windowProps = 'width=375,height=700,menubar=no,location=no';
      const url = `${target}?${qs.stringify(params)}`;
      this.certWindow = window.open(url, 'pass-window', windowProps);
      window.removeEventListener('message', this.handleMessage, false);
      window.addEventListener('message', this.handleMessage, false);
    }
  }

  @action.bound
  protected handleMessage(payload: { data: { type: string } }): void {
    const certPermittedType = ['cert-complete', 'cert-failed'];
    const hyCardPermittedType = ['hy-join-complete', 'hy-join-failed'];
    if (certPermittedType.includes(payload.data?.type) && this.certWindow) {
      this.certWindow.close();
      this.certWindow = null;
      if (payload.data.type === 'cert-complete') {
        this.onCICertSuccess();
      } else if (payload.data.type === 'cert-failed') {
        this.onCICertError();
      }
      return;
    }

    if (hyCardPermittedType.includes(payload.data?.type) && this.hyCardWindow) {
      this.hyCardWindow.close();
      this.hyCardWindow = null;
      const isIssued = payload.data.type === 'hy-join-complete';
      if (isIssued) {
        this.isHyCardIssued = true;
      }
      this.waitForCardIssued?.complete(isIssued);
    }
  }

  @action.bound
  onCICertSuccess(): void {
    this.waitForCI?.complete(true);
  }

  @action.bound
  onCICertError(): void {
    this.waitForCI?.complete(false);
  }

  get IsHyCardIssued(): boolean {
    return this.isHyCardIssued;
  }

  @action.bound
  async agreeToMarketingTerms(): Promise<void> {
    if (!this.isLoggedIn) {
      this.login();
      return;
    }

    const waitForMarketingAgree = new Future<boolean>();

    const yaNative = getYaNative();

    if (!yaNative.isEnabled) {
      const deepLink = 'yanoljamotel://etc/bottomsheet/notificationsettings';
      redirectTo(deepLink, this.globalStore.Device);
      return;
    }

    if (!this.marketingTermsStore.IsAgreedMarketing) {
      // 마케팅 수신 동의가 안된 경우
      this.marketingTermsStore.isOpen = true;
      this.marketingTermsStore.callback = (result) => {
        // 마케팅 수신 동의 닫기
        if (result) {
          waitForMarketingAgree.complete(true);
          this.marketingTermsStore.isOpen = false;
        } else {
          waitForMarketingAgree.complete(false);
        }
      };
      if (!(await waitForMarketingAgree.get())) {
        return;
      }
    } else {
      this.globalStore.toast.addItem({
        key: 'MARKETING_ALREADY_AGREED',
        text: '이벤트·혜택 알림 수신 동의 상태입니다.',
        timeout: 5000,
      });
    }

    if (yaNative.isEnabled) {
      await this.validateDevicePush();
      if (!this.isDevicePushAgreed) {
        await this.requestPushAuthorization();
      }
    }
  }

  @action.bound
  async validateDevicePush(): Promise<void> {
    try {
      this.isDevicePushAgreed = (await this.getPushStatus()).isAuthorized;
    } catch (e) {
      this.isDevicePushAgreed = false;
      this.globalStore.toast.addItem({
        key: 'fail-to-get-device-push-status',
        text: '정보를 불러오지 못하였습니다',
        timeout: 2000,
      });
    }
  }

  @action.bound
  getPushStatus(): Promise<PushStatusCallbackParam> {
    return new Promise<PushStatusCallbackParam>((resolve, reject) => {
      const yaNative = getYaNative();
      if (yaNative.isEnabled) {
        yaNative.pushStatus((success, result) => {
          if (success) {
            resolve(result);
          } else {
            reject();
          }
        });
      }
    });
  }

  @action.bound
  async requestPushAuthorization(): Promise<void> {
    try {
      const result = await new Promise<RequestPushAuthorizationCallbackParam>((resolve, reject) => {
        const yaNative = getYaNative();
        if (yaNative.isEnabled) {
          yaNative.requestPushAuthorization((success, result) => {
            if (success) {
              resolve(result);
            } else {
              reject();
            }
          });
        }
      });
      this.isDevicePushAgreed = result.isAuthorized;
    } catch (e) {
      this.globalStore.toast.addItem({
        key: 'fail-to-request-push-authorization',
        text: '정보를 불러오지 못하였습니다',
        timeout: 2000,
      });
    }
  }

  @action.bound
  onVersion3TabClick(index: number): void {
    const tabHeight = 64;
    const navHeight = 48;
    const smartBannerHeight = this.showSmartBanner ? 86 : 0;
    const offset = 3; // 대체 어디서 발생하는건지 모르겠는데 3픽셀 어긋난다
    // ref 영역에 display:contents 를 적용하면 offsetTop 이 0으로 나온다. 따라서 display:contents를 적용하지 않은 영역까지 내려가서 offsetTop을 구한다.
    // v4 현재 ref -> impression -> gridItem 구조
    const anchorTab =
      index === 0
        ? (this.anchorTab0Ref?.current?.firstChild?.firstChild as HTMLElement)
        : index === 1
        ? (this.anchorTab1Ref?.current?.firstChild?.firstChild as HTMLElement)
        : index === 2
        ? (this.anchorTab2Ref?.current as HTMLElement)
        : (this.anchorTab3Ref?.current as HTMLElement);
    if (anchorTab) {
      const to = anchorTab.offsetTop - tabHeight - navHeight - smartBannerHeight + offset;
      scrollWithAnim(to);
    }

    const tabNames = ['NOL 쿠폰 15%', '놀페페 코인 10만 페이백', 'NOL 첫 결제 2만 청구 할인', 'NOL포인트 10%'];
    void Kinesis.click(this.pageName, {
      object: 'tabName',
      objectType: tabNames[index],
      motherPageVersion: this.version,
    });
  }

  @action.bound
  onClickEverlandBenfit(): void {
    const yaNative = getYaNative();
    if (yaNative.isEnabled) {
      yaNative.deepLink({ link: 'yanoljamotel://wv?url=https://content.yanolja.com/event/1576' });
    } else {
      window.location.href = 'https://content.yanolja.com/event/1576';
    }
  }
}

export const NolPromotionStoreCtx = createContext<NolPromotionStore>(null!);
